import React, { useCallback, useRef, useState } from 'react';
import '../primitives/tome/tome-tokens.css';
import type { EyeStyleId } from '../../../shared/types/AppearanceTypes';
import type { GhostProps } from '../../../utils/GhostGenerator';
import { CREATOR_THEME_VARS } from '../theme';
import { CharacterPortrait } from '../components/CharacterPortrait';
import { resolvePlayerEquipmentVisuals } from '../../../shared/equipmentVisuals';
import '../../../client/styles/login.css';

import {
  ACCESSORIES,
  AURAS,
  COLORS,
  EYES,
  EYE_STYLES,
  MARKINGS,
  MOUTHS,
  SHAPES,
  TRAILS,
} from './CharacterCustomizer/CharacterCustomizer.constants';
import {
  CREATION_CALLINGS,
  findCreationCalling,
} from './CharacterCustomizer/CharacterCustomizer.callings';
import type { CreationCallingId } from './CharacterCustomizer/CharacterCustomizer.callings';
import { CallingCard } from './CharacterCustomizer/components/CallingCard';
import { CallingLorePanel } from './CharacterCustomizer/components/CallingLorePanel';
import { BodyTab } from './CharacterCustomizer/tabs/BodyTab';
import { FaceTab } from './CharacterCustomizer/tabs/FaceTab';
import { StyleTab } from './CharacterCustomizer/tabs/StyleTab';

interface CharacterCustomizerProps {
  onCreate: (name: string, runtimeClassId: string, appearance: GhostProps) => void;
  onCancel: () => void;
  /** Shown only when the account has heroes to return to (stream note 48). */
  onBackToCharacters?: () => void;
  initialName?: string;
  error?: string;
  submitting?: boolean;
}

type CosmeticTab = 'body' | 'face' | 'style';

const CREATOR_TABS: ReadonlyArray<{
  id: CosmeticTab;
  label: string;
  hint: string;
}> = [
  { id: 'body', label: 'Body', hint: 'Color & form' },
  { id: 'face', label: 'Face', hint: 'Expression' },
  { id: 'style', label: 'Style', hint: 'Finishing touches' },
];

// The creator preview is intentionally unequipped. Player equipment visuals
// are resolved from owned item IDs after creation; cosmetic appearance must
// never manufacture a starter weapon or armor silhouette.
const EMPTY_CREATOR_EQUIPMENT = resolvePlayerEquipmentVisuals({});

const rand = <T,>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];

const RANDOM_NAME_STEMS = [
  'Grimwald',
  'Sable',
  'Thorne',
  'Mira',
  'Kael',
  'Lyra',
  'Bron',
  'Zara',
  'Orin',
  'Fenn',
  'Nyx',
  'Rook',
  'Wren',
  'Dax',
  'Vex',
  'Aldric',
  'Pip',
  'Quill',
  'Ash',
  'Juno',
] as const;

function randomHeroName(): string {
  return `${rand(RANDOM_NAME_STEMS)}${Math.floor(Math.random() * 90 + 10)}`.slice(0, 12);
}

function randomCallingId(): CreationCallingId {
  return rand(CREATION_CALLINGS).id;
}

function randomAppearance(): GhostProps {
  return {
    color: rand(COLORS),
    eyes: rand(EYES) as GhostProps['eyes'],
    mouth: rand(MOUTHS) as GhostProps['mouth'],
    accessory: (Math.random() > 0.3 ? rand(ACCESSORIES) : 'none') as GhostProps['accessory'],
    shape: rand(SHAPES) as GhostProps['shape'],
    bodySize: 'normal',
    aura: (Math.random() > 0.7 ? rand(AURAS) : 'none') as GhostProps['aura'],
    blush: Math.random() > 0.5,
    faceMarking: (Math.random() > 0.5 ? rand(MARKINGS) : 'none') as GhostProps['faceMarking'],
    trail: (Math.random() > 0.7 ? rand(TRAILS) : 'none') as GhostProps['trail'],
    opacity: 'solid',
    actionPose: false,
    eyeStyle: { style: rand(EYE_STYLES) as EyeStyleId },
  };
}

/** Keep the character-create payload cosmetic-only, even if a future caller
 * supplies renderer or legacy equipment fields to this component. */
function playerCreationAppearance(appearance: GhostProps): GhostProps {
  return {
    color: appearance.color,
    eyes: appearance.eyes,
    mouth: appearance.mouth,
    accessory: appearance.accessory,
    shape: appearance.shape,
    bodySize: appearance.bodySize,
    aura: appearance.aura,
    blush: appearance.blush,
    faceMarking: appearance.faceMarking,
    trail: appearance.trail,
    opacity: appearance.opacity,
    actionPose: appearance.actionPose,
    eyeStyle: appearance.eyeStyle,
    ghostEyes: appearance.ghostEyes,
  };
}

export const CharacterCustomizer: React.FC<CharacterCustomizerProps> = ({
  onCreate,
  onCancel,
  onBackToCharacters,
  initialName,
  error,
  submitting = false,
}) => {
  const [activeTab, setActiveTab] = useState<CosmeticTab>('body');
  const ghostRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(() => initialName || randomHeroName());
  const [callingId, setCallingId] = useState<CreationCallingId>(() => (
    initialName ? 'warrior' : randomCallingId()
  ));
  const [appearance, setAppearance] = useState<GhostProps>(randomAppearance);
  const [validationError, setValidationError] = useState('');
  const selectedCalling = findCreationCalling(callingId) ?? CREATION_CALLINGS[0];

  const randomize = useCallback((scope: 'all' | 'tab' = 'all') => {
    if (scope === 'all') {
      setName(randomHeroName());
      setCallingId(randomCallingId());
      setValidationError('');
    }

    setAppearance(previous => {
      const next = { ...previous };

      if (scope === 'all' || activeTab === 'body') {
        next.color = rand(COLORS);
        next.shape = rand(SHAPES) as GhostProps['shape'];
        next.bodySize = rand(['small', 'normal', 'large'] as const);
        next.aura = (Math.random() > 0.7 ? rand(AURAS) : 'none') as GhostProps['aura'];
      }
      if (scope === 'all' || activeTab === 'face') {
        next.eyes = rand(EYES) as GhostProps['eyes'];
        next.eyeStyle = { style: rand(EYE_STYLES) as EyeStyleId };
        next.mouth = rand(MOUTHS) as GhostProps['mouth'];
        next.faceMarking = (Math.random() > 0.5 ? rand(MARKINGS) : 'none') as GhostProps['faceMarking'];
        next.blush = Math.random() > 0.5;
      }
      if (scope === 'all' || activeTab === 'style') {
        next.accessory = (Math.random() > 0.3 ? rand(ACCESSORIES) : 'none') as GhostProps['accessory'];
        next.trail = (Math.random() > 0.7 ? rand(TRAILS) : 'none') as GhostProps['trail'];
      }

      return next;
    });
  }, [activeTab]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const trimmedName = name.trim();

    if (!trimmedName) {
      setValidationError('Give your hero a name before entering the world.');
      nameInputRef.current?.focus();
      return;
    }

    setValidationError('');
    onCreate(trimmedName, selectedCalling.runtimeClassId, playerCreationAppearance(appearance));
  };

  const testAction = () => {
    const ghost = ghostRef.current;
    if (!ghost) return;

    ghost.classList.remove('anim-melee', 'anim-ranged', 'anim-magic');
    void ghost.offsetWidth;
    ghost.classList.add(selectedCalling.previewAnimation);
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    let nextIndex: number | undefined;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % CREATOR_TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + CREATOR_TABS.length) % CREATOR_TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = CREATOR_TABS.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = CREATOR_TABS[nextIndex];
    setActiveTab(nextTab.id);
    requestAnimationFrame(() => document.getElementById(`cc-tab-${nextTab.id}`)?.focus());
  };

  const creatorError = validationError || error;

  return (
    <div className="cc-container tome-tokens" style={CREATOR_THEME_VARS}>
      <main className="cc-layout">
        <section
          className="cc-preview-panel"
          aria-labelledby="cc-screen-title"
          style={{ '--cc-calling-accent': selectedCalling.accent } as React.CSSProperties}
        >
          <header className="cc-preview-heading">
            {onBackToCharacters && (
              <button type="button" className="cc-back" onClick={onBackToCharacters}>
                <span aria-hidden="true">←</span> Back to characters
              </button>
            )}
            <span className="cc-eyebrow">Character creation</span>
            <h1 className="cc-title" id="cc-screen-title">Forge Your Legend</h1>
            <p className="cc-subtitle">Choose your calling, then shape the spirit who will answer it.</p>
          </header>

          <div className="cc-preview-stage">
            <div className="cc-preview-ring" aria-hidden="true" />
            <div
              ref={ghostRef}
              className={`cc-ghost-wrapper ${appearance.actionPose ? 'action-pose' : ''}`}
            >
              <CharacterPortrait
                appearance={appearance}
                characterClass={selectedCalling.runtimeClassId}
                equipmentVisuals={EMPTY_CREATOR_EQUIPMENT}
                size={300}
                label="Your hero preview"
              />
            </div>
            <div className="cc-preview-badge">
              <strong>{selectedCalling.name}</strong>
              <span>{selectedCalling.epithet}</span>
            </div>
          </div>

          <div className="cc-preview-actions" aria-label="Preview controls">
            <button type="button" className="cc-preview-action" onClick={testAction}>
              <span aria-hidden="true">⚔</span> Preview move
            </button>
            <button type="button" className="cc-preview-action" onClick={() => randomize('all')}>
              <span aria-hidden="true">⚄</span> Surprise me
            </button>
          </div>

          <CallingLorePanel callingId={callingId} />
        </section>

        <form className="cc-controls-panel" onSubmit={handleSubmit} noValidate>
          <header className="cc-controls-heading">
            <div>
              <span className="cc-eyebrow">Identity & appearance</span>
              <h2>Build your hero</h2>
            </div>
            <span className="cc-cosmetic-note">Cosmetic choices</span>
          </header>

          <div className="cc-controls-scroll">
            {creatorError && (
              <div className="cc-error-box" role="alert">{creatorError}</div>
            )}

            <section className="cc-form-section" aria-labelledby="cc-identity-title">
              <div className="cc-form-section-heading">
                <div>
                  <span className="cc-step-number">01</span>
                  <h3 id="cc-identity-title">Name your hero</h3>
                </div>
                <span>Up to 12 characters</span>
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="cc-character-name">Character name</label>
                <input
                  ref={nameInputRef}
                  id="cc-character-name"
                  className="cc-input"
                  value={name}
                  onChange={event => {
                    setName(event.target.value);
                    if (validationError) setValidationError('');
                  }}
                  placeholder="Hero name"
                  maxLength={12}
                  aria-invalid={Boolean(validationError)}
                />
              </div>
            </section>

            <section className="cc-form-section" aria-labelledby="cc-calling-title">
              <div className="cc-form-section-heading">
                <div>
                  <span className="cc-step-number">02</span>
                  <h3 id="cc-calling-title">Choose a calling</h3>
                </div>
                <span>Nine paths, one beginning</span>
              </div>
              <div className="cc-calling-grid">
                {CREATION_CALLINGS.map(calling => (
                  <CallingCard
                    key={calling.id}
                    callingId={calling.id}
                    icon={calling.icon}
                    name={calling.name}
                    epithet={calling.epithet}
                    accent={calling.accent}
                    selected={callingId === calling.id}
                    onSelect={() => setCallingId(calling.id)}
                  />
                ))}
              </div>
            </section>

            <section className="cc-form-section cc-form-section--appearance" aria-labelledby="cc-appearance-title">
              <div className="cc-form-section-heading">
                <div>
                  <span className="cc-step-number">03</span>
                  <h3 id="cc-appearance-title">Make them yours</h3>
                </div>
                <button type="button" className="cc-inline-randomize" onClick={() => randomize('tab')}>
                  Randomize this tab
                </button>
              </div>

              <div className="cc-tabs" role="tablist" aria-label="Appearance category" aria-orientation="horizontal">
                {CREATOR_TABS.map((tab, index) => (
                  <button
                    key={tab.id}
                    id={`cc-tab-${tab.id}`}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`cc-panel-${tab.id}`}
                    tabIndex={activeTab === tab.id ? 0 : -1}
                    className={`cc-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                    onKeyDown={event => handleTabKeyDown(event, index)}
                  >
                    <strong>{tab.label}</strong>
                    <span>{tab.hint}</span>
                  </button>
                ))}
              </div>

              <div
                id={`cc-panel-${activeTab}`}
                className="cc-tab-content"
                role="tabpanel"
                aria-labelledby={`cc-tab-${activeTab}`}
              >
                {activeTab === 'body' && (
                  <BodyTab appearance={appearance} setAppearance={setAppearance} />
                )}
                {activeTab === 'face' && (
                  <FaceTab appearance={appearance} setAppearance={setAppearance} />
                )}
                {activeTab === 'style' && (
                  <StyleTab appearance={appearance} setAppearance={setAppearance} />
                )}
              </div>
            </section>
          </div>

          <footer className="cc-actions">
            <div className="cc-action-summary" aria-live="polite">
              <strong>{name.trim() || 'Unnamed hero'}</strong>
              <span>{selectedCalling.name} · {selectedCalling.epithet}</span>
            </div>
            <div className="cc-action-buttons">
              <button type="button" className="cc-btn cc-btn-secondary" onClick={onCancel}>Cancel</button>
              <button type="submit" className="cc-btn cc-btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Character'}
              </button>
            </div>
          </footer>
        </form>
      </main>
    </div>
  );
};
